// ScheduleColumn.js
import React, { useState, useEffect, useRef, useMemo, useCallback, useImperativeHandle, forwardRef } from 'react';
import './MatchSchedule.css';
import TextField from '@mui/material/TextField';
import { useDrag, useDrop } from 'react-dnd';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { CalendarIcon } from '@mui/x-date-pickers';
import { ExpandLess, ExpandMore, Label } from '@mui/icons-material';
import DropZone from './DropZone';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ScheduleColumnSummary from './ScheduleColumnSummary';
import DroppableJob from './DroppableJob';
import { IconButton } from '@mui/material';
import CentralCalls from '../../centralCalls';
import CircularProgress from '@mui/material/CircularProgress';

// ScheduleColumn Component
const ScheduleColumn = forwardRef(({ refreshColumnByDate,employees, columnIndex, selectedDate, crewColors, isPastDue, refreshTrigger, isCollapsed,
                                     filterPhaseStatus, filterPhaseType, filterBuilder, filterCrew }, ref) => {
  // Notify MatchSchedule of the initial date
  const [isLoading, setIsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);

  const [initialized, setInitialized] = useState(false);

  // --- ordering helpers ---
const dateKey = useMemo(() => selectedDate.format('YYYY-MM-DD'), [selectedDate]);

// When exactly one crew is selected, we keep order per (date, crew)
const singleCrewOid = useMemo(
  () => (Array.isArray(filterCrew) && filterCrew.length === 1 ? Number(filterCrew[0]) : null),
  [filterCrew]
);

const arrayMove = (arr, from, to) => {
  const copy = arr.slice();
  const [moved] = copy.splice(from, 1);
  copy.splice(to, 0, moved);
  return copy;
};

// Order UI jobs by a saved arrayOrder of phase_status_oid
const sortJobsByArrayOrder = (jobsList, arrayOrder) => {
  if (!Array.isArray(arrayOrder) || arrayOrder.length === 0) return jobsList;
  const idx = new Map(arrayOrder.map((id, i) => [id, i]));
  const BIG = 1e9;
  return [...jobsList].sort(
    (a, b) => (idx.get(a.phase_status_oid) ?? BIG) - (idx.get(b.phase_status_oid) ?? BIG)
  );
};

const insertAtIndex = (arr, item, index) => {
  const copy = arr.slice();
  const safeIndex = Math.max(0, Math.min(index, copy.length));
  copy.splice(safeIndex, 0, item);
  return copy;
};

const normalizeDropDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value?.format === 'function') return value.format('YYYY-MM-DD');
  return dayjs(value).format('YYYY-MM-DD');
};

useEffect(() => {
  const timer = setTimeout(() => {
    setInitialized(true);
  }, 250); // 250ms = quarter second

  return () => clearTimeout(timer); // cleanup if unmounted
}, []);

useEffect(() => {
  const handleScheduleMove = (event) => {
    const payload = event?.detail;
    if (!payload) return;

    const {
      sourceDate,
      targetDate,
      targetIndex,
      phase_status_oid,
      movedJob,
    } = payload;

    if (!phase_status_oid) return;

    if (sourceDate === dateKey && targetDate !== dateKey) {
      setJobs(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];
        return safePrev.filter(j => j.phase_status_oid !== phase_status_oid);
      });
      return;
    }

    if (targetDate === dateKey && sourceDate !== dateKey && movedJob) {
      setJobs(prev => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const exists = safePrev.some(j => j.phase_status_oid === phase_status_oid);
        if (exists) return prev;

        const hasAssignee =
          !!movedJob?.team_oid ||
          !!movedJob?.employee_team_oid ||
          (Array.isArray(movedJob?.employee_oids) && movedJob.employee_oids.length > 0);
        const isRealScheduledDate = targetDate !== '1900-01-01';
        const optimisticReadyStatus =
          hasAssignee && isRealScheduledDate ? 'scheduled' : movedJob?.ready_status;

        const normalized = {
          ...movedJob,
          date: targetDate,
          date_scheduled: targetDate === '1900-01-01' ? null : targetDate,
          ready_status: optimisticReadyStatus,
        };
        return insertAtIndex(safePrev, normalized, targetIndex);
      });
    }
  };

  window.addEventListener('schedule3-job-moved', handleScheduleMove);
  return () => window.removeEventListener('schedule3-job-moved', handleScheduleMove);
}, [dateKey]);


  useEffect(() => {
      console.log("Fetching jobs for", selectedDate.format('YYYY-MM-DD'), {
    columnIndex,
    filterPhaseType,
    filterCrew,
    filterPhaseStatus,
    filterBuilder,
    refreshTrigger
  });
    const fetchJobs = async () => {
      if (!initialized) return;
      setIsLoading(true); // start loading
     
      const formattedNewDate = CentralCalls.sqlFormatDate2(selectedDate);
      // console.log({
      //   date: formattedNewDate,
      //   isPastDue: columnIndex === 0 ? 1 : 0,
      //   phaseType: filterPhaseType?.length > 0 ? filterPhaseType : [],
      //   readyStat: filterPhaseStatus?.length > 0 ? filterPhaseStatus : [],
      //   builderOid: filterBuilder?.length > 0 ? filterBuilder.map(Number) : [],
      //   crews: filterCrew?.length > 0 ? filterCrew.map(Number) : [],
      // });
      const data = await CentralCalls.callStoredProcedureWithParams('sp_getOpenScheduleByDate', {
        date: formattedNewDate,
        isPastDue: columnIndex === 0 ? 1 : 0,
        phaseType: filterPhaseType?.length > 0 ? filterPhaseType : [],
        readyStat: filterPhaseStatus?.length > 0 ? filterPhaseStatus : [],
        builderOid: filterBuilder?.length > 0 ? filterBuilder.map(Number) : [],
        crews: filterCrew?.length > 0 ? filterCrew.map(Number) : [],
      });
      
      // Apply saved order IMMEDIATELY if filtering to single crew
      if (singleCrewOid && data?.length > 0) {
        const res = await CentralCalls.callStoredProcedureWithParams(
          'sp_getCrewSortOrder',
          { job_date: dateKey, employee_team_oid: singleCrewOid }
        );
        const saved = Array.isArray(res?.[0]?.arrayOrder) ? res[0].arrayOrder : null;
        
        if (saved && saved.length > 0) {
          const sorted = sortJobsByArrayOrder(data, saved);
          setJobs(sorted);
        } else {
          // No saved order - create one from current data
          const currentIds = data.map(j => j.phase_status_oid).filter(Boolean);
          await CentralCalls.callStoredProcedureWithParams(
            'sp_upsertCrewSortOrder',
            { job_date: dateKey, employee_team_oid: singleCrewOid, arrayOrder: currentIds }
          );
          setJobs(data);
        }
      } else {
        setJobs(Array.isArray(data) ? data : []);
      }
      
      setIsLoading(false); // end loading
    };
  
    fetchJobs();
}, [initialized, selectedDate, columnIndex, filterPhaseType, filterCrew, filterPhaseStatus, filterBuilder, refreshTrigger, singleCrewOid, dateKey]);


const moveJob = useCallback((item, sourceDate, sourceIndex, targetDate, targetIndex) => {
  const normalizedSourceDate = normalizeDropDate(sourceDate);
  const normalizedTargetDate = normalizeDropDate(targetDate);

  const isSameDate = normalizedSourceDate === normalizedTargetDate;
  const inSingleCrewMode = !!singleCrewOid;

  if (isSameDate && inSingleCrewMode) {
    const safeJobs = Array.isArray(jobs) ? jobs : [];
    console.log(safeJobs);
    setJobs(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      const next = arrayMove(safePrev, sourceIndex, targetIndex);
      // normalize indexes across the entire list
      return next.map((j, i) => ({ ...j, index: i }));
    });
    
    // compute order from the *new* state synchronously via source/target indexes
    // (or just recompute from the optimistic "next" array you already built)
    const optimisticOrder = arrayMove(safeJobs, sourceIndex, targetIndex)
      .map(j => j.phase_status_oid)
      .filter(Boolean);
    console.log('Optimistic order:', optimisticOrder);
    console.log('saving... datekey:', dateKey, ', singleCrewoid:', singleCrewOid, ', OpimtistOREDER:', optimisticOrder);
    CentralCalls.callStoredProcedureWithParams(
      'sp_upsertCrewSortOrder',
      {  job_date: dateKey, employee_team_oid: singleCrewOid, arrayOrder: optimisticOrder }
    ).catch((error) => {
      console.error('Failed to persist crew sort order:', error);
      refreshColumnByDate?.(dateKey);
    });

    return;
  }

  if (!isSameDate) {
    const movedJob = item?.job || jobs?.[sourceIndex] || null;
    window.dispatchEvent(new CustomEvent('schedule3-job-moved', {
      detail: {
        sourceDate: normalizedSourceDate,
        targetDate: normalizedTargetDate,
        targetIndex,
        phase_status_oid: item?.phase_status_oid,
        movedJob,
      }
    }));
  }

  if (item?.phase_oid) {
    const dateScheduled = normalizedTargetDate === '1900-01-01' ? null : CentralCalls.sqlFormatDate2(normalizedTargetDate);
    CentralCalls.upsertData(
      { oid: item.phase_oid, date_scheduled: dateScheduled },
      'phase',
      'update'
    ).catch((error) => {
      console.error('Failed to persist moved job date:', error);
      refreshColumnByDate?.(normalizedTargetDate);
      refreshColumnByDate?.(normalizedSourceDate);
    });
  }

  item.index = targetIndex;
  item.date = normalizedTargetDate;
}, [jobs, singleCrewOid, dateKey, refreshColumnByDate]);


  const assignCrewToJob = useCallback((date, jobIndex, crewIndex, crew) => {
    if (!crew) return;

    const currentJob = jobs?.[jobIndex];
    if (!currentJob) return;

    const scheduledDate = date === '1900-01-01' ? null : CentralCalls.sqlFormatDate2(date);

    // Optimistically update local state so assignment feels instant.
    setJobs(prevJobs => {
      const nextJobs = [...prevJobs];
      const targetJob = nextJobs[jobIndex];
      if (!targetJob) return prevJobs;

      const nextDateScheduled = date === '1900-01-01' ? null : date;
      const nextReadyStatus = crew?.team_oid && nextDateScheduled ? 'scheduled' : targetJob.ready_status;

      nextJobs[jobIndex] = {
        ...targetJob,
        employee_oids: crew.employee_oids,
        employee_team_oid: crew.team_oid,
        team_oid: crew.team_oid,
        team_name: crew.team_name,
        team_color_code: crew.hex_color_code,
        date_scheduled: nextDateScheduled,
        date: date,
        ready_status: nextReadyStatus,
        crewIndex,
      };

      return nextJobs;
    });

    // Save in the background; keep UI responsive and avoid full refresh spinner.
    (async () => {
      try {
        await CentralCalls.upsertData(
          { oid: currentJob.phase_status_oid, employee_oids: JSON.stringify(crew.employee_oids), employee_team_oid: crew.team_oid },
          'phase_status',
          'update'
        );

        await CentralCalls.upsertData(
          { oid: currentJob.phase_oid, date_scheduled: scheduledDate },
          'phase',
          'update'
        );
      } catch (error) {
        console.error('Failed to assign crew to job:', error);
        // Pull fresh data only on failure to recover UI/backend consistency.
        refreshColumnByDate?.(date);
      }
    })();
  }, [jobs, refreshColumnByDate]);

  const unassignCrewFromJob = useCallback((date, jobIndex) => {
    const currentJob = jobs?.[jobIndex];
    if (!currentJob) return;

    setJobs(prevJobs => {
      const safePrev = Array.isArray(prevJobs) ? prevJobs : [];
      const nextJobs = [...safePrev];
      const targetJob = nextJobs[jobIndex];
      if (!targetJob) return prevJobs;

      nextJobs[jobIndex] = {
        ...targetJob,
        employee_oids: null,
        employee_team_oid: null,
        team_oid: null,
        team_name: null,
        team_color_code: null,
        ready_status: 'open',
        crewIndex: null,
      };

      return nextJobs;
    });

    CentralCalls.upsertData(
      { oid: currentJob.phase_status_oid, employee_oids: null, employee_team_oid: null },
      'phase_status',
      'update'
    ).catch((error) => {
      console.error('Failed to remove crew from job:', error);
      refreshColumnByDate?.(date);
    });
  }, [jobs, refreshColumnByDate]);


  const dropZones = useMemo(() => (
    jobs?.map((job, index) => (
      <React.Fragment key={index}>
        <DropZone
          key={`drop-${job.phase_status_oid}`}
          date={selectedDate.format('YYYY-MM-DD')}
          index={index}
          moveJob={moveJob}
          assignCrewToJob={assignCrewToJob}
        />

        <DroppableJob
          key={`job-${job.phase_status_oid}`}
          job={job}
          index={index}
          date={selectedDate}
          crewColors={crewColors}
          moveJob={moveJob}
          assignCrewToJob={assignCrewToJob}
          unassignCrewFromJob={unassignCrewFromJob}
          employees={employees}
          setRefresh={refreshColumnByDate}
          isPastDue={isPastDue}
        />
      </React.Fragment>
    ))
  ), [jobs, selectedDate, moveJob, assignCrewToJob, unassignCrewFromJob, crewColors, employees, refreshColumnByDate, isPastDue]);
  // Expose getJobs() to parent
  useImperativeHandle(ref, () => ({
    getJobs: () => jobs
   }));
  // const assignCrewToJob = async (date, jobIndex, crewIndex) => {
  //     const job = jobs[jobIndex];
  //     const crew = '[' + crews[crewIndex].employees.map(emp => emp.oid) + ']';

  //     CentralCalls.upsertData({ oid: job.phase_status_oid, employee_oids: crew }, 'phase_status', 'update');
  //     CentralCalls.upsertData({ oid: job.phase_oid, date_scheduled: CentralCalls.sqlFormatdate(date) }, 'phase', 'update');

  // }
  // const handleDateChange = (newDate) => {
  //   setSelectedDate(newDate);
  // }; 
  return (
    <div style={{ height: '80%' }}>
      {!isCollapsed && (<div style={{ marginLeft: '15px', marginRight: '15px', marginTop: 0, marginBottom: '15px', padding: 0 }}>
        <span >
          <ScheduleColumnSummary
            total={jobs?.length}
            totalComplete={jobs?.filter(job => job.date_scheduled != null && job.team_oid != null)?.length}
          />
        </span>
      </div>)}
      <div className={"match-schedule-column schedule " + (isCollapsed ? "collapsed" : "")}>

        {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        
        {!isPastDue && !isCollapsed && (
          <span className='title'>{selectedDate.format('ddd')} {selectedDate.format('MM-DD')}</span>
        )}
        {isPastDue && !isCollapsed && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span className='title' style={{ marginLeft: '15px', textDecoration: 'underline' }}>
              Past Due
            </span>
          </div>
        )}
        <IconButton onClick={onToggleCollapse} size="small">
          {
            isCollapsed
              ? isPastDue
                ? <WarningIcon />
                : <CalendarIcon />
              : <ExpandLess />
          }
        </IconButton>
      </div> */}
        {!isCollapsed && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* {!isPastDue && (
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField {...params} style={{ marginBottom: '20px' }} />
                )} />
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <IconButton onClick={() => setSelectedDate(dayjs(selectedDate).add(1, 'day'))} size="small" style={{ marginLeft: '10px' }}>
                  <AddIcon />
                </IconButton>
                <IconButton onClick={() => setSelectedDate(dayjs(selectedDate).subtract(1, 'day'))} size="small" style={{ marginLeft: '10px' }}>
                  <RemoveIcon />
                </IconButton>
              </div>
            </div>
          )} */}

            {/* <ScheduleColumnSummary total={jobs?.length} totalComplete={jobs?.filter(job => job.date_scheduled != null)?.length} ></ScheduleColumnSummary> */}
            {/* {jobs?.length === 0 ? (
            <DropZone
              date={selectedDate}
              index={0}
              moveJob={moveJob}
              assignCrewToJob={assignCrewToJob}
            />
          ) : (
            jobs?.map((job, index) => (
              <React.Fragment key={index}>
                <DropZone
                  date={selectedDate}
                  index={index}
                  moveJob={moveJob}
                  assignCrewToJob={assignCrewToJob}

                />
                <DroppableJob
                  job={job}
                  index={index}
                  date={selectedDate}
                  crewColors={crewColors}
                  moveJob={moveJob}
                  assignCrewToJob={assignCrewToJob}
                  employees={employees}
                  setRefresh={setRefresh}
                  isPastDue={isPastDue}

                />
              </React.Fragment>
            ))
          )}
          {jobs?.length > 0 && (
            <DropZone
              date={selectedDate}
              index={jobs.length}
              moveJob={moveJob}
              assignCrewToJob={assignCrewToJob}
            />
          )} */}
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress size={30} />
              </div>
            ) : (
              <>
                {dropZones}
                <DropZone
                  key={`drop-tail-${dateKey}`}
                  date={selectedDate.format('YYYY-MM-DD')}
                  index={jobs ? jobs.length : 0}
                  moveJob={moveJob}
                  assignCrewToJob={assignCrewToJob}
                  extend={true}
                />
              </>
            )}
          </LocalizationProvider>
        )}
      </div>
    </div>
  );
});
export default React.memo(ScheduleColumn);